/**
 * GoalTrackingTab Component
 * Redesigned Overview tab with 5-zone layout for goal tracking
 *
 * Zone 1: The Headline - Status indicator (Behind/Ahead/On Track)
 * Zone 2: The Scoreboard - Current/Projected/Gap cards
 * Zone 3: The Pipeline - Funnel with health indicators
 * Zone 4: The Diagnosis - 4-panel analysis
 * Zone 5: The Scenarios - Calculator with preview above sliders
 *
 * Created: January 14, 2026
 * Based on: OverviewTabRedesign.js (December 29, 2025)
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  calculate6MonthRollingAverage,
  calculateCurrentMonthRates,
  compareMetrics,
  analyzeConversionImpact
} from '../utils/trendingCalculations';

// ============================================================
// ENHANCED SLIDER COMPONENT (Phase 7)
// Shows Current / Adjusted / Change + 6-month benchmark + status
// ============================================================

function EnhancedSlider({
  label,
  icon,
  currentValue,      // Actual current rate (readonly)
  adjustedValue,     // Slider value (state)
  onAdjust,          // Handler function
  historicalAvg,     // 6-month benchmark
  min,
  max,
  step,
  formatValue,       // e.g., (v) => `${v.toFixed(1)}%`
  formatChange,      // e.g., (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}pp`
  isPercentChange = false  // For activity slider: show % change instead of pp
}) {
  const change = adjustedValue - currentValue;
  const vsHistorical = currentValue - historicalAvg;

  const getStatus = () => {
    if (vsHistorical > 1) return { icon: '✅', text: 'Above average', color: '#22c55e' };
    if (vsHistorical < -1) return { icon: '🔴', text: `Below average (${vsHistorical.toFixed(1)}pp)`, color: '#ef4444' };
    return { icon: '➖', text: 'At average', color: '#64748b' };
  };

  const status = getStatus();

  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      padding: '16px',
      border: '1px solid #e2e8f0'
    }}>
      {/* Title Row */}
      <div style={{
        fontSize: '14px',
        fontWeight: 600,
        color: '#1e293b',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <span>{icon}</span>
        <span>{label}</span>
      </div>

      {/* Values Row */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '10px',
        fontSize: '12px',
        flexWrap: 'wrap'
      }}>
        <div>
          <span style={{ color: '#64748b' }}>Current: </span>
          <span style={{ fontWeight: 600, color: '#1e293b' }}>
            {formatValue(currentValue)}
          </span>
        </div>
        <div>
          <span style={{ color: '#64748b' }}>Adjusted: </span>
          <span style={{
            fontWeight: 600,
            color: Math.abs(adjustedValue - currentValue) > 0.01 ? '#3b82f6' : '#1e293b'
          }}>
            {formatValue(adjustedValue)}
          </span>
        </div>
        <div>
          <span style={{ color: '#64748b' }}>Change: </span>
          <span style={{
            fontWeight: 600,
            color: change > 0.01 ? '#22c55e' : change < -0.01 ? '#ef4444' : '#64748b'
          }}>
            {formatChange(change)}
          </span>
        </div>
      </div>

      {/* Slider */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={adjustedValue}
        onChange={(e) => onAdjust(parseFloat(e.target.value))}
        style={{ width: '100%', cursor: 'pointer', marginBottom: '8px' }}
      />

      {/* Benchmark Row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '11px',
        color: '#64748b'
      }}>
        <span>📊 6-mo avg: {formatValue(historicalAvg)}</span>
        <span style={{ color: status.color, fontWeight: 500 }}>
          {status.icon} {status.text}
        </span>
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function GoalTrackingTab({
  data,
  goals,
  reps,
  month,
  year = new Date().getFullYear(),
  monthYear,
  selectedRep,
  setSelectedRep,
  developerMode
}) {
  // Calculator state (Phase 3.5)
  const [calculatorInputs, setCalculatorInputs] = useState({
    activityIncrease: 20,
    actToAppsRate: null, // Will be initialized from data
    appsToApprovedRate: null,
    approvedToFundedRate: null
  });

  // Active scenario state (Phase 6 - Clickable Scenarios)
  const [activeScenario, setActiveScenario] = useState(null); // 'A', 'B', 'C', or null

  // Daily Breakdown state (copied from This Week tab)
  const [expandedDay, setExpandedDay] = useState(null);
  const [dailyBreakdownCollapsed, setDailyBreakdownCollapsed] = useState(false);

  // Helper: Use monthYear for goals (e.g., "January 2026"), fallback to legacy format
  const goalMonth = monthYear || `${month} ${year}`;

  // Derive month index from month name prop (used by businessDaysInfo and revenuePacingData)
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthIndex = monthNames.indexOf(month);

  // ============================================================
  // DAILY BREAKDOWN HELPERS (copied from This Week tab)
  // ============================================================

  const ACTIVITY_FORMULA_CUTOVER_DATE = '2025-12-16';

  const calculateActivityPoints = (calls, sms, apps, date) => {
    if (date && date < ACTIVITY_FORMULA_CUTOVER_DATE) {
      return calls + (apps * 10);
    }
    return calls + sms + (apps * 5);
  };

  // Get current week date range (Monday - Sunday)
  const getWeekDates = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return { monday, sunday, today };
  };

  const { monday: weekMonday, sunday: weekSunday, today: weekToday } = getWeekDates();

  // Calculate days of week with dates (5 business days Mon-Fri only)
  const daysOfWeek = useMemo(() => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date(weekMonday);
      date.setDate(weekMonday.getDate() + i);
      days.push({
        name: date.toLocaleDateString('en-US', { weekday: 'long' }),
        shortName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        date: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
        fullDate: date.toISOString().split('T')[0],
        isToday: date.toDateString() === weekToday.toDateString(),
        isPast: date < weekToday && date.toDateString() !== weekToday.toDateString(),
        isFuture: date > weekToday
      });
    }
    return days;
  }, [weekMonday, weekToday]);

  // Toggle day expansion for Daily Breakdown
  const toggleDayExpansion = (fullDate) => {
    setExpandedDay(expandedDay === fullDate ? null : fullDate);
  };

  // Get week's data for selected rep or all reps
  const weekData = useMemo(() => {
    if (!data || !data.tabs) return null;

    const isAllReps = selectedRep === 'All Reps' || selectedRep === 'Team';

    const result = {};
    daysOfWeek.forEach(day => {
      const dayData = {
        calls: 0,
        sms: 0,
        appsIn: 0,
        approved: 0,
        funded: 0,
        revenue: 0,
        activityCount: 0
      };

      // Get calls
      const calls = data.tabs.Dials_TalkTime?.data?.filter(r =>
        (isAllReps ? reps.includes(r.Rep) : r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      dayData.calls = calls.reduce((s,r) => s+(parseInt(r.TotalCalls)||0), 0);

      // Get SMS
      const sms = data.tabs.SMS?.data?.filter(r =>
        (isAllReps ? reps.includes(r.Rep) : r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      const smsCount = sms.reduce((sum, s) => sum + (parseFloat(s.TotalSMS) || 0), 0);
      dayData.sms = smsCount;

      // Get Apps In
      const appsIn = data.tabs.Apps_In?.data?.filter(r =>
        (isAllReps ? reps.includes(r.Rep) : r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      dayData.appsIn = appsIn.length;

      // Get Approved
      const approved = data.tabs.Approved?.data?.filter(r =>
        (isAllReps ? reps.includes(r.Rep) : r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      dayData.approved = approved.length;

      // Get Funded
      const funded = data.tabs.Funded?.data?.filter(r =>
        (isAllReps ? reps.includes(r.Rep) : r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      dayData.funded = funded.length;

      // Get Revenue (from Funded tab: Commission + Placement_Fee)
      const revRecords = data.tabs.Funded?.data?.filter(r =>
        (isAllReps ? reps.includes(r.Rep) : r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      dayData.revenue = revRecords.reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);

      // Calculate Activity Points
      dayData.activityCount = calculateActivityPoints(dayData.calls, dayData.sms, dayData.appsIn, day.fullDate);

      result[day.name] = dayData;
    });

    return result;
  }, [data, daysOfWeek, selectedRep, reps, calculateActivityPoints]);

  // Calculate weekly metrics
  const weeklyMetrics = useMemo(() => {
    if (!weekData) return null;

    const isAllReps = selectedRep === 'All Reps' || selectedRep === 'Team';

    // Get goals
    let activityCallGoal = 0;
    if (goals) {
      if (isAllReps) {
        const allRepsGoals = goals.filter(g => g.Month === goalMonth);
        activityCallGoal = allRepsGoals.reduce((sum, g) => sum + parseInt(g.Activity_Calls_Goal || g.Activity_Call_Goal || 0), 0);
      } else {
        const repGoals = goals.find(g => g.Rep === selectedRep && g.Month === goalMonth);
        if (repGoals) {
          activityCallGoal = parseInt(repGoals.Activity_Calls_Goal || repGoals.Activity_Call_Goal || 0);
        }
      }
    }

    // Activity_Call_Goal is already a daily value — no division needed
    const dailyActivityTarget = activityCallGoal;

    // Sum weekly totals
    let totalCalls = 0, totalSMS = 0, totalApps = 0, totalApproved = 0, totalFunded = 0, totalRevenue = 0, totalActivityCount = 0;
    daysOfWeek.forEach(day => {
      const dayData = weekData[day.name];
      if (dayData) {
        totalCalls += dayData.calls;
        totalSMS += dayData.sms;
        totalApps += dayData.appsIn;
        totalApproved += dayData.approved;
        totalFunded += dayData.funded;
        totalRevenue += dayData.revenue;
        totalActivityCount += dayData.activityCount;
      }
    });

    return {
      totals: {
        calls: totalCalls,
        sms: totalSMS,
        apps: totalApps,
        approved: totalApproved,
        funded: totalFunded,
        revenue: totalRevenue,
        activityCount: totalActivityCount
      },
      dailyTargets: {
        calls: dailyActivityTarget
      },
      activityCallGoal: dailyActivityTarget
    };
  }, [weekData, goals, selectedRep, goalMonth, daysOfWeek]);

  // Get per-rep breakdown for a specific day
  const getRepBreakdownForDay = useCallback((fullDate) => {
    if (!data || !data.tabs) return [];

    return reps.map(rep => {
      const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === goalMonth);
      const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Calls_Goal || repGoalRow.Activity_Call_Goal || 0) : 0;

      const calls = data.tabs.Dials_TalkTime?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];

      const sms = data.tabs.SMS?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];
      const smsCount = sms.reduce((sum, s) => sum + (parseFloat(s.TotalSMS) || 0), 0);

      const appsIn = data.tabs.Apps_In?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];

      const approved = data.tabs.Approved?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];

      const funded = data.tabs.Funded?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];

      const revRecords = data.tabs.Funded?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];
      const totalRevenue = revRecords.reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);

      const activityCount = calculateActivityPoints(calls.reduce((s,r) => s+(parseInt(r.TotalCalls)||0), 0), smsCount, appsIn.length, fullDate);

      return {
        rep,
        calls: calls.reduce((s,r) => s+(parseInt(r.TotalCalls)||0), 0),
        sms: smsCount,
        appsIn: appsIn.length,
        approved: approved.length,
        funded: funded.length,
        revenue: totalRevenue,
        activityCount: Math.round(activityCount),
        activityGoal: Math.round(repActivityGoal)
      };
    });
  }, [data, reps, goals, goalMonth, calculateActivityPoints]);

  // Get per-rep breakdown for entire week
  const getRepBreakdownForWeek = useCallback(() => {
    if (!data || !data.tabs) return [];

    const weekDateStrings = daysOfWeek.map(d => d.fullDate);

    return reps.map(rep => {
      const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === goalMonth);
      const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Calls_Goal || repGoalRow.Activity_Call_Goal || 0) * 5 : 0;

      let totalCalls = 0, totalSMS = 0, totalAppsIn = 0, totalApproved = 0, totalFunded = 0, totalRevenue = 0, totalActivity = 0;

      weekDateStrings.forEach(fullDate => {
        const calls = data.tabs.Dials_TalkTime?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];
        totalCalls += calls.reduce((s,r) => s+(parseInt(r.TotalCalls)||0), 0);

        const sms = data.tabs.SMS?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];
        const smsCount = sms.reduce((sum, s) => sum + (parseFloat(s.TotalSMS) || 0), 0);
        totalSMS += smsCount;

        const appsIn = data.tabs.Apps_In?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];
        totalAppsIn += appsIn.length;

        const approved = data.tabs.Approved?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];
        totalApproved += approved.length;

        const funded = data.tabs.Funded?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];
        totalFunded += funded.length;

        const revRecords = data.tabs.Funded?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];
        totalRevenue += revRecords.reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);

        totalActivity += calculateActivityPoints(calls.reduce((s,r) => s+(parseInt(r.TotalCalls)||0), 0), smsCount, appsIn.length, fullDate);
      });

      return {
        rep,
        calls: totalCalls,
        sms: totalSMS,
        appsIn: totalAppsIn,
        approved: totalApproved,
        funded: totalFunded,
        revenue: totalRevenue,
        activityCount: Math.round(totalActivity),
        activityGoal: Math.round(repActivityGoal)
      };
    });
  }, [data, reps, goals, goalMonth, daysOfWeek, calculateActivityPoints]);

  const isAllReps = selectedRep === 'All Reps' || selectedRep === 'Team';

  // ============================================================
  // BUSINESS DAYS CALCULATIONS
  // ============================================================

  const businessDaysInfo = useMemo(() => {
    const today = new Date();
    const currentMonthIdx = today.getMonth();
    const currentYear = today.getFullYear();
    const isCurrentMonth = monthIndex === currentMonthIdx && year === currentYear;
    const isPastMonth = (year < currentYear) || (year === currentYear && monthIndex < currentMonthIdx);
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();

    let elapsed = 0;
    let total = 0;

    for (let day = 1; day <= lastDay; day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        total++;
        if (isPastMonth || (isCurrentMonth && day <= today.getDate())) {
          elapsed++;
        }
      }
    }

    return {
      elapsed,
      total,
      remaining: total - elapsed
    };
  }, [year, monthIndex]);

  // ============================================================
  // HELPER: Filter data by rep (Team = all reps, otherwise specific rep)
  // ============================================================

  const filterByRep = useCallback((records, repFilter) => {
    if (!records) return [];
    if (repFilter === 'Team' || repFilter === 'All Reps') {
      return records.filter(r => reps.includes(r.Rep));
    }
    return records.filter(r => r.Rep === repFilter);
  }, [reps]);

  // ============================================================
  // HELPER: Filter data by month AND year (uses Date column)
  // ============================================================

  const filterByMonthYear = useCallback((dataArray) => {
    return dataArray?.filter(row =>
      row.Month === month && row.Date && new Date(row.Date).getFullYear() === year
    ) || [];
  }, [month, year]);

  // ============================================================
  // CONVERSION ANALYSIS (Phase 4 - Worst Conversion Identifier)
  // ============================================================

  const conversionAnalysis = useMemo(() => {
    if (!data || !data.tabs || businessDaysInfo.elapsed === 0) return null;
    return analyzeConversionImpact(data, selectedRep, month, reps, businessDaysInfo.elapsed);
  }, [data, selectedRep, month, reps, businessDaysInfo.elapsed]);

  // ============================================================
  // KPI METRICS CALCULATION (Phase 2.1)
  // ============================================================

  const kpiMetrics = useMemo(() => {
    if (!data || !data.tabs || !goals) {
      return null;
    }

    const isTeam = selectedRep === 'Team' || selectedRep === 'All Reps';

    // Filter data for current month, year, and selected rep
    const revenueData = filterByRep(
      filterByMonthYear(data.tabs.Funded?.data),
      selectedRep
    );
    const fundedData = filterByRep(
      filterByMonthYear(data.tabs.Funded?.data),
      selectedRep
    );
    const appsData = filterByRep(
      filterByMonthYear(data.tabs.Apps_In?.data),
      selectedRep
    );
    const callsData = filterByRep(
      filterByMonthYear(data.tabs.Dials_TalkTime?.data),
      selectedRep
    );
    const smsData = filterByRep(
      filterByMonthYear(data.tabs.SMS?.data),
      selectedRep
    );

    // Current values
    const revenue = revenueData.reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);
    const fundedDollars = fundedData.reduce((sum, r) => sum + (parseFloat(r.Amount) || 0), 0);
    const fundedUnits = fundedData.length;
    const appsIn = appsData.length;
    const calls = callsData.reduce((s,r) => s+(parseInt(r.TotalCalls)||0), 0);
    // SMS uses SUM of TotalSMS field from aggregate rows
    const sms = smsData.reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);
    // Activity Points = Calls + SMS + (Apps × 5) [post Dec 16, 2025 formula]
    const activityPoints = calls + sms + (appsIn * 5);

    // Goals - aggregate for team or get for individual rep
    const currentMonthGoals = goals.filter(g => g.Month === goalMonth);
    let revenueGoal = 0;
    let fundedGoal = 0;
    let fundedUnitsGoal = 0;
    let appsGoal = 0;
    let activityGoalDaily = 0;

    if (isTeam) {
      // Aggregate all reps' goals
      currentMonthGoals
        .filter(g => reps.includes(g.Rep))
        .forEach(g => {
          revenueGoal += parseInt(g.Revenue_Goal) || 0;
          fundedGoal += parseInt(g.Funded_Goal) || 0;
          fundedUnitsGoal += parseInt(g.Funded_Units_Goal) || 0;
          appsGoal += parseInt(g.Apps_Goal) || 0;
          activityGoalDaily += parseInt(g.Activity_Call_Goal) || 0;
        });
    } else {
      // Find specific rep's goals
      const repGoal = currentMonthGoals.find(g => g.Rep === selectedRep);
      if (repGoal) {
        revenueGoal = parseInt(repGoal.Revenue_Goal) || 0;
        fundedGoal = parseInt(repGoal.Funded_Goal) || 0;
        fundedUnitsGoal = parseInt(repGoal.Funded_Units_Goal) || 0;
        appsGoal = parseInt(repGoal.Apps_Goal) || 0;
        activityGoalDaily = parseInt(repGoal.Activity_Call_Goal) || 0;
      }
    }

    // Activity_Call_Goal is DAILY - multiply by business days for monthly goal
    const activityGoal = activityGoalDaily * businessDaysInfo.total;

    // Calculate expected values (prorated by business days elapsed)
    const paceRatio = businessDaysInfo.total > 0 ? businessDaysInfo.elapsed / businessDaysInfo.total : 0;
    const revenueExpected = revenueGoal * paceRatio;
    const fundedDollarsExpected = fundedGoal * paceRatio;
    const fundedUnitsExpected = fundedUnitsGoal * paceRatio;
    const activityExpected = activityGoal * paceRatio;
    const appsExpected = appsGoal * paceRatio;

    return {
      revenue: {
        current: revenue,
        expected: revenueExpected,
        goal: revenueGoal,
        diff: revenue - revenueExpected,
        isCurrency: true
      },
      fundedDollars: {
        current: fundedDollars,
        expected: fundedDollarsExpected,
        goal: fundedGoal,
        diff: fundedDollars - fundedDollarsExpected,
        isCurrency: true
      },
      fundedUnits: {
        current: fundedUnits,
        expected: fundedUnitsExpected,
        goal: fundedUnitsGoal,
        diff: fundedUnits - fundedUnitsExpected,
        isCurrency: false
      },
      activityPoints: {
        current: activityPoints,
        expected: activityExpected,
        goal: activityGoal,
        diff: activityPoints - activityExpected,
        isCurrency: false
      },
      appsIn: {
        current: appsIn,
        expected: appsExpected,
        goal: appsGoal,
        diff: appsIn - appsExpected,
        isCurrency: false
      }
    };
  }, [data, goals, month, selectedRep, reps, businessDaysInfo, filterByRep, filterByMonthYear, goalMonth]);

  // ============================================================
  // FORMATTING HELPERS (wrapped in useCallback to prevent re-renders)
  // ============================================================

  const formatCurrency = useCallback((value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${Math.round(value).toLocaleString()}`;
  }, []);

  const formatNumber = useCallback((value) => {
    return Math.round(value).toLocaleString();
  }, []);

  // Full currency format for KPI cards (no K/M abbreviation)
  const formatCurrencyFull = useCallback((value) => {
    return `$${Math.round(value).toLocaleString()}`;
  }, []);

  // Full diff format for KPI cards (no K/M abbreviation)
  const formatDiffFull = useCallback((diff, isCurrency) => {
    const absValue = Math.abs(diff);
    if (isCurrency) {
      return `$${Math.round(absValue).toLocaleString()}`;
    }
    return Math.round(absValue).toLocaleString();
  }, []);

  // ============================================================
  // ACTIVITY TRENDS CALCULATION (Phase 2.2)
  // ============================================================

  const activityTrends = useMemo(() => {
    if (!data || !data.tabs || businessDaysInfo.elapsed === 0) {
      return null;
    }

    // Calculate current month per-day rates
    const currentRates = calculateCurrentMonthRates(data, selectedRep, month, businessDaysInfo.elapsed);

    // Calculate 6-month rolling averages for each metric
    const callsAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Dials_TalkTime', field: null });
    const smsAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'SMS', field: 'Count' });
    const appsAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Apps_In', field: null });
    const approvedAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Approved', field: null });
    const revenueAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Funded', fields: ['Commission', 'Placement_Fee'] });

    // Calculate Activity Points per day (current and historical)
    // Activity = Calls + SMS + (Apps × 5)
    const activityPerDay = currentRates.callsPerDay + currentRates.smsPerDay + (currentRates.appsPerDay * 5);
    const activityAvgPerDay = callsAvg.avgPerDay + smsAvg.avgPerDay + (appsAvg.avgPerDay * 5);

    // Compare current to historical
    const comparisons = {
      activity: compareMetrics(activityPerDay, activityAvgPerDay),
      calls: compareMetrics(currentRates.callsPerDay, callsAvg.avgPerDay),
      sms: compareMetrics(currentRates.smsPerDay, smsAvg.avgPerDay),
      apps: compareMetrics(currentRates.appsPerDay, appsAvg.avgPerDay),
      approved: compareMetrics(currentRates.approvedPerDay, approvedAvg.avgPerDay),
      revenue: compareMetrics(currentRates.revenuePerDay, revenueAvg.avgPerDay)
    };

    return comparisons;
  }, [data, selectedRep, month, businessDaysInfo.elapsed]);

  // ============================================================
  // LAST 5 BUSINESS DAYS SPARKLINE DATA (Phase 2.2)
  // ============================================================

  const sparklineData = useMemo(() => {
    if (!data || !data.tabs) {
      return null;
    }

    // Get last 5 business days
    const getLast5BusinessDays = () => {
      const days = [];
      const today = new Date();
      let count = 0;
      let daysBack = 0;

      while (count < 5 && daysBack < 14) {
        const date = new Date(today);
        date.setDate(today.getDate() - daysBack);
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          days.unshift(date.toISOString().split('T')[0]); // Format: YYYY-MM-DD
          count++;
        }
        daysBack++;
      }
      return days;
    };

    const last5Days = getLast5BusinessDays();

    // Helper to get daily totals
    const getDailyTotals = (tabName, aggregationType, field = null) => {
      const tabData = data.tabs[tabName]?.data || [];
      return last5Days.map(dateStr => {
        const dayRecords = tabData.filter(r => {
          const recordDate = r.Date?.split('T')[0];
          const matchesDate = recordDate === dateStr;
          const matchesRep = (selectedRep === 'Team' || selectedRep === 'All Reps')
            ? reps.includes(r.Rep)
            : r.Rep === selectedRep;
          return matchesDate && matchesRep;
        });

        if (aggregationType === 'count') {
          return dayRecords.length;
        }
        if (aggregationType === 'sum' && field) {
          return dayRecords.reduce((sum, r) => sum + (parseFloat(r[field]) || 0), 0);
        }
        return 0;
      });
    };

    // Get daily data for each metric
    const callsDaily = getDailyTotals('Dials_TalkTime', 'count');
    const smsDaily = getDailyTotals('SMS', 'sum', 'Count');
    const appsDaily = getDailyTotals('Apps_In', 'count');
    const approvedDaily = getDailyTotals('Approved', 'count');
    const commissionDaily = getDailyTotals('Funded', 'sum', 'Commission');
    const placementFeeDaily = getDailyTotals('Funded', 'sum', 'Placement_Fee');
    const revenueDaily = commissionDaily.map((v, i) => v + placementFeeDaily[i]);

    // Calculate activity points for each day
    const activityDaily = last5Days.map((_, i) =>
      callsDaily[i] + smsDaily[i] + (appsDaily[i] * 5)
    );

    return {
      days: last5Days,
      activity: activityDaily,
      calls: callsDaily,
      sms: smsDaily,
      apps: appsDaily,
      approved: approvedDaily,
      revenue: revenueDaily
    };
  }, [data, selectedRep, reps]);

  // ============================================================
  // PATH TO GOAL DATA (Phase 3)
  // ============================================================

  const pathToGoalData = useMemo(() => {
    if (!data || !data.tabs || !goals || businessDaysInfo.total === 0 || businessDaysInfo.elapsed === 0) {
      return null;
    }

    const isTeam = selectedRep === 'Team' || selectedRep === 'All Reps';

    // Get current month data (with year filter from Date column)
    const revenueRecords = filterByRep(
      filterByMonthYear(data.tabs.Funded?.data),
      selectedRep
    );
    const fundedRecords = filterByRep(
      filterByMonthYear(data.tabs.Funded?.data),
      selectedRep
    );
    const approvedRecords = filterByRep(
      filterByMonthYear(data.tabs.Approved?.data),
      selectedRep
    );
    const appsRecords = filterByRep(
      filterByMonthYear(data.tabs.Apps_In?.data),
      selectedRep
    );
    const callsRecords = filterByRep(
      filterByMonthYear(data.tabs.Dials_TalkTime?.data),
      selectedRep
    );
    const smsRecords = filterByRep(
      filterByMonthYear(data.tabs.SMS?.data),
      selectedRep
    );

    // Current counts
    const revenue = revenueRecords.reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);
    const funded = fundedRecords.length;
    const approved = approvedRecords.length;
    const apps = appsRecords.length;
    const calls = callsRecords.reduce((s,r) => s+(parseInt(r.TotalCalls)||0), 0);
    const sms = smsRecords.reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);
    const activity = calls + sms + (apps * 5);

    // Get 6-month historical averages for conversion rates (moved before current rates so histAvgDealSize can be used as fallback)
    const historicalAppsToApproved = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Approved', field: null });
    const historicalApps = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Apps_In', field: null });
    const historicalFunded = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Funded', field: null });
    const historicalRevenue = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Funded', fields: ['Commission', 'Placement_Fee'] });
    const historicalCalls = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Dials_TalkTime', field: null });
    const historicalSms = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'SMS', field: 'Count' });

    // Calculate historical conversion rates (based on per-day averages)
    const histAppsPerDay = historicalApps.avgPerDay || 0;
    const histApprovedPerDay = historicalAppsToApproved.avgPerDay || 0;
    const histFundedPerDay = historicalFunded.avgPerDay || 0;
    const histRevenuePerDay = historicalRevenue.avgPerDay || 0;
    const histCallsPerDay = historicalCalls.avgPerDay || 0;
    const histSmsPerDay = historicalSms.avgPerDay || 0;
    const histActivityPerDay = histCallsPerDay + histSmsPerDay + (histAppsPerDay * 5);

    const histActToAppsRate = histActivityPerDay > 0 ? (histAppsPerDay / histActivityPerDay) * 100 : 1.9;
    const histAppsToApprovedRate = histAppsPerDay > 0 ? (histApprovedPerDay / histAppsPerDay) * 100 : 63.5;
    const histApprovedToFundedRate = histApprovedPerDay > 0 ? (histFundedPerDay / histApprovedPerDay) * 100 : 50.5;
    const histAvgDealSize = histFundedPerDay > 0 ? histRevenuePerDay / histFundedPerDay : 4500;

    // Calculate conversion rates (current month)
    const activityToAppsRate = activity > 0 ? (apps / activity) * 100 : 0;
    const appsToApprovedRate = apps > 0 ? (approved / apps) * 100 : 0;
    const approvedToFundedRate = approved > 0 ? (funded / approved) * 100 : 0;
    // FIX: Use historical avg deal size as fallback when no funded deals (prevents $0 projections for new reps)
    const avgDealSize = funded > 0 ? revenue / funded : histAvgDealSize;

    // Get revenue goal
    const currentMonthGoals = goals.filter(g => g.Month === goalMonth);
    let revenueGoal = 0;
    if (isTeam) {
      currentMonthGoals.filter(g => reps.includes(g.Rep)).forEach(g => {
        revenueGoal += parseInt(g.Revenue_Goal) || 0;
      });
    } else {
      const repGoal = currentMonthGoals.find(g => g.Rep === selectedRep);
      revenueGoal = repGoal ? (parseInt(repGoal.Revenue_Goal) || 0) : 0;
    }

    // Calculate projections (linear: current / elapsed * total)
    const dailyRate = revenue / businessDaysInfo.elapsed;
    const projectedRevenue = dailyRate * businessDaysInfo.total;
    const projectedFunded = Math.round((funded / businessDaysInfo.elapsed) * businessDaysInfo.total);
    const projectedApproved = Math.round((approved / businessDaysInfo.elapsed) * businessDaysInfo.total);
    const projectedApps = Math.round((apps / businessDaysInfo.elapsed) * businessDaysInfo.total);
    const projectedActivity = Math.round((activity / businessDaysInfo.elapsed) * businessDaysInfo.total);
    const gapToGoal = revenueGoal - projectedRevenue;
    const percentOfGoal = revenueGoal > 0 ? (projectedRevenue / revenueGoal) * 100 : 0;

    // Calculate deals needed to close gap
    const dealsNeeded = avgDealSize > 0 ? Math.ceil(gapToGoal / avgDealSize) : 0;

    // Scenario calculations
    const remainingDays = businessDaysInfo.remaining;

    // Scenario A: Volume Play (+20% activity, same conversion rates)
    const scenarioA = (() => {
      const activityPerDay = activity / businessDaysInfo.elapsed;
      const boostedActivity = activityPerDay * 1.2 * remainingDays; // +20% for remaining days
      const extraApps = boostedActivity * (activityToAppsRate / 100);
      const extraApproved = extraApps * (appsToApprovedRate / 100);
      const extraFunded = extraApproved * (approvedToFundedRate / 100);
      const extraRevenue = extraFunded * avgDealSize;
      return {
        impact: extraRevenue,
        extraApps: Math.round(extraApps),
        extraFunded: Math.round(extraFunded),
        closesPercent: gapToGoal > 0 ? (extraRevenue / gapToGoal) * 100 : 0,
        projectedTotal: projectedRevenue + extraRevenue,
        projectedPercent: revenueGoal > 0 ? ((projectedRevenue + extraRevenue) / revenueGoal) * 100 : 0
      };
    })();

    // Scenario B: Conversion Fix (dynamically targets the WORST conversion by revenue impact)
    // Uses analyzeConversionImpact from Phase 3 to identify which conversion hurts most
    const scenarioB = (() => {
      // Get worst conversion from conversionAnalysis (calculated in separate useMemo)
      const worstConv = conversionAnalysis?.worstConversion;

      // If no worst conversion (all healthy or no data), use fallback
      if (!worstConv) {
        return {
          impact: 0,
          currentRate: appsToApprovedRate,
          targetRate: histAppsToApprovedRate,
          extraFunded: 0,
          closesPercent: 0,
          conversionName: null,  // Signals "all healthy" state
          isAllHealthy: true
        };
      }

      // Use the revenue impact and details from the worst conversion
      return {
        impact: worstConv.revenueImpact,
        currentRate: worstConv.currentRate,
        targetRate: worstConv.historicalRate,
        extraFunded: Math.round(worstConv.extraDeals),
        closesPercent: gapToGoal > 0 ? (worstConv.revenueImpact / gapToGoal) * 100 : 0,
        conversionName: worstConv.name,  // Dynamic: 'Activity → Apps', 'Apps → Approved', or 'Approved → Funded'
        isAllHealthy: false,
        projectedTotal: projectedRevenue + worstConv.revenueImpact,
        projectedPercent: revenueGoal > 0 ? ((projectedRevenue + worstConv.revenueImpact) / revenueGoal) * 100 : 0
      };
    })();

    // Scenario C: Hybrid (both volume and conversion)
    const scenarioC = (() => {
      const combinedImpact = scenarioA.impact + scenarioB.impact;
      const combinedFunded = scenarioA.extraFunded + scenarioB.extraFunded;
      const projectedTotal = projectedRevenue + combinedImpact;
      return {
        impact: combinedImpact,
        extraFunded: combinedFunded,
        closesPercent: gapToGoal > 0 ? (combinedImpact / gapToGoal) * 100 : 0,
        projectedTotal,
        projectedPercent: revenueGoal > 0 ? (projectedTotal / revenueGoal) * 100 : 0
      };
    })();

    return {
      // Current metrics
      activity,
      apps,
      approved,
      funded,
      revenue,
      // Conversion rates
      activityToAppsRate,
      appsToApprovedRate,
      approvedToFundedRate,
      avgDealSize,
      // Historical rates
      histActToAppsRate,
      histAppsToApprovedRate,
      histApprovedToFundedRate,
      histAvgDealSize,
      // Projections
      projectedRevenue,
      projectedFunded,
      projectedApproved,
      projectedApps,
      projectedActivity,
      revenueGoal,
      gapToGoal,
      percentOfGoal,
      dealsNeeded,
      // Scenarios
      scenarioA,
      scenarioB,
      scenarioC
    };
  }, [data, goals, month, selectedRep, reps, businessDaysInfo, filterByRep, filterByMonthYear, goalMonth, conversionAnalysis]);

  // ============================================================
  // TODAY'S ACTIVITY METRICS (Phase 6 - Daily Accountability)
  // Shows team totals for TODAY vs daily goals
  // ============================================================

  const todayMetrics = useMemo(() => {
    if (!data || !data.tabs || !goals) return null;

    // Get today's date
    const today = new Date();
    const dayOfWeek = today.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    // If weekend, show Friday's data
    let targetDate = today;
    if (isWeekend) {
      const fridayOffset = dayOfWeek === 0 ? 2 : 1; // Sunday: 2 back, Saturday: 1 back
      targetDate = new Date(today);
      targetDate.setDate(today.getDate() - fridayOffset);
    }

    // Use LOCAL date (not UTC) to avoid timezone issues
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const targetDateStr = `${year}-${month}-${day}`;

    // Filter helper for target date — respects selectedRep
    const isAllRepsToday = selectedRep === 'All Reps' || selectedRep === 'Team';
    const filterForDate = (records) => {
      return (records || []).filter(r => {
        const recordDate = r.Date?.split('T')[0];
        const matchesDate = recordDate === targetDateStr;
        const matchesRep = isAllRepsToday ? reps.includes(r.Rep) : r.Rep === selectedRep;
        return matchesDate && matchesRep;
      });
    };

    // Get data for target date
    const callsToday = filterForDate(data.tabs.Dials_TalkTime?.data);
    const smsToday = filterForDate(data.tabs.SMS?.data);
    const appsToday = filterForDate(data.tabs.Apps_In?.data);
    const approvedToday = filterForDate(data.tabs.Approved?.data);
    const fundedToday = filterForDate(data.tabs.Funded?.data);

    // Calculate actuals
    const calls = callsToday.reduce((s,r) => s+(parseInt(r.TotalCalls)||0), 0);
    const sms = smsToday.reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);
    const apps = appsToday.length;
    const approved = approvedToday.length;
    const funded = fundedToday.length;
    const revenue = fundedToday.reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);
    const activity = calls + sms + (apps * 5);

    // Calculate daily goals (Team aggregate)
    const currentMonthGoals = goals.filter(g => g.Month === goalMonth);
    let dailyRevenueGoal = 0;
    let dailyFundedGoal = 0;
    let dailyAppsGoal = 0;
    let dailyActivityGoal = 0;

    if (isAllRepsToday) {
      currentMonthGoals
        .filter(g => reps.includes(g.Rep))
        .forEach(g => {
          dailyRevenueGoal += (parseInt(g.Revenue_Goal) || 0) / businessDaysInfo.total;
          dailyFundedGoal += (parseInt(g.Funded_Units_Goal) || 0) / businessDaysInfo.total;
          dailyAppsGoal += (parseInt(g.Apps_Goal) || 0) / businessDaysInfo.total;
          dailyActivityGoal += parseInt(g.Activity_Call_Goal) || 0;
        });
    } else {
      const repGoal = currentMonthGoals.find(g => g.Rep === selectedRep);
      if (repGoal) {
        dailyRevenueGoal = (parseInt(repGoal.Revenue_Goal) || 0) / businessDaysInfo.total;
        dailyFundedGoal = (parseInt(repGoal.Funded_Units_Goal) || 0) / businessDaysInfo.total;
        dailyAppsGoal = (parseInt(repGoal.Apps_Goal) || 0) / businessDaysInfo.total;
        dailyActivityGoal = parseInt(repGoal.Activity_Call_Goal) || 0;
      }
    }

    // Format date for display
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const displayDateStr = `${dayNames[targetDate.getDay()]}, ${monthNames[targetDate.getMonth()]} ${targetDate.getDate()}`;

    // Calculate % contribution to activity
    // Activity = Calls + SMS + (Apps × 5)
    const callsContrib = activity > 0 ? (calls / activity) * 100 : 0;
    const smsContrib = activity > 0 ? (sms / activity) * 100 : 0;
    const appsContrib = activity > 0 ? ((apps * 5) / activity) * 100 : 0;

    return {
      date: targetDate,
      displayDate: displayDateStr,
      isWeekend,
      showingFriday: isWeekend,
      isAllReps: isAllRepsToday,
      // Order: Activity first, then the components that build it, then outcomes
      metrics: [
        { key: 'activity', actual: activity, goal: Math.round(dailyActivityGoal), label: 'Activity', isActivity: true },
        { key: 'calls', actual: calls, goal: null, label: 'Calls', activityContrib: callsContrib },
        { key: 'sms', actual: sms, goal: null, label: 'SMS', activityContrib: smsContrib },
        { key: 'apps', actual: apps, goal: Math.round(dailyAppsGoal), label: 'Apps', activityContrib: appsContrib, appsMultiplier: 5 },
        { key: 'approved', actual: approved, goal: null, label: 'Approved' },
        { key: 'funded', actual: funded, goal: Math.round(dailyFundedGoal), label: 'Funded' },
        { key: 'revenue', actual: revenue, goal: Math.round(dailyRevenueGoal), label: 'Revenue', isCurrency: true }
      ]
    };
  }, [data, goals, reps, selectedRep, goalMonth, businessDaysInfo.total]);

  // ============================================================
  // CALCULATOR PROJECTIONS (Phase 3.5)
  // ============================================================

  const calculatorProjections = useMemo(() => {
    if (!pathToGoalData) return null;

    // Use current rates as defaults if calculator inputs aren't set yet
    const actIncrease = calculatorInputs.activityIncrease;
    const actToApps = calculatorInputs.actToAppsRate ?? pathToGoalData.activityToAppsRate;
    const appsToApproved = calculatorInputs.appsToApprovedRate ?? pathToGoalData.appsToApprovedRate;
    const approvedToFunded = calculatorInputs.approvedToFundedRate ?? pathToGoalData.approvedToFundedRate;

    // FIX: Project activity to end of month FIRST, then apply boost
    // Previously used current MTD activity, causing scenario to show lower numbers than baseline
    const projectedActivity = businessDaysInfo.elapsed > 0
      ? (pathToGoalData.activity / businessDaysInfo.elapsed) * businessDaysInfo.total
      : pathToGoalData.activity;
    const boostedActivity = projectedActivity * (1 + actIncrease / 100);
    const projApps = boostedActivity * (actToApps / 100);
    const projApproved = projApps * (appsToApproved / 100);
    const projFunded = projApproved * (approvedToFunded / 100);
    const projRevenue = projFunded * pathToGoalData.avgDealSize;

    const gapRemaining = pathToGoalData.revenueGoal - projRevenue;
    const percentToGoal = pathToGoalData.revenueGoal > 0 ? (projRevenue / pathToGoalData.revenueGoal) * 100 : 0;

    return {
      projApps: Math.round(projApps),
      projApproved: Math.round(projApproved),
      projFunded: Math.round(projFunded),
      projRevenue,
      gapRemaining,
      percentToGoal
    };
  }, [pathToGoalData, calculatorInputs, businessDaysInfo]);

  // ============================================================
  // SCENARIO CLICK HANDLERS (Phase 6 - Clickable Scenarios)
  // ============================================================

  // Handler for Scenario A (Volume Play): +20% activity, keep current conversions
  const handleScenarioA = useCallback(() => {
    if (!pathToGoalData) return;
    setCalculatorInputs({
      activityIncrease: 20,
      actToAppsRate: pathToGoalData.activityToAppsRate,
      appsToApprovedRate: pathToGoalData.appsToApprovedRate,
      approvedToFundedRate: pathToGoalData.approvedToFundedRate
    });
    setActiveScenario('A');
  }, [pathToGoalData]);

  // Handler for Scenario B (Fix Worst Conversion): fix worst conversion to historical
  const handleScenarioB = useCallback(() => {
    if (!pathToGoalData) return;
    const worst = conversionAnalysis?.worstConversion;
    if (!worst) return; // All healthy, nothing to fix

    // Start with current values
    const newInputs = {
      activityIncrease: 0, // No activity boost
      actToAppsRate: pathToGoalData.activityToAppsRate,
      appsToApprovedRate: pathToGoalData.appsToApprovedRate,
      approvedToFundedRate: pathToGoalData.approvedToFundedRate
    };

    // Fix the worst conversion to its historical rate
    if (worst.name === 'Activity → Apps') {
      newInputs.actToAppsRate = worst.historicalRate;
    } else if (worst.name === 'Apps → Approved') {
      newInputs.appsToApprovedRate = worst.historicalRate;
    } else if (worst.name === 'Approved → Funded') {
      newInputs.approvedToFundedRate = worst.historicalRate;
    }

    setCalculatorInputs(newInputs);
    setActiveScenario('B');
  }, [pathToGoalData, conversionAnalysis]);

  // Handler for Scenario C (Hybrid): +20% activity AND fix worst conversion
  const handleScenarioC = useCallback(() => {
    if (!pathToGoalData) return;
    const worst = conversionAnalysis?.worstConversion;

    // Start with current values + activity boost
    const newInputs = {
      activityIncrease: 20,
      actToAppsRate: pathToGoalData.activityToAppsRate,
      appsToApprovedRate: pathToGoalData.appsToApprovedRate,
      approvedToFundedRate: pathToGoalData.approvedToFundedRate
    };

    // Fix the worst conversion if there is one
    if (worst) {
      if (worst.name === 'Activity → Apps') {
        newInputs.actToAppsRate = worst.historicalRate;
      } else if (worst.name === 'Apps → Approved') {
        newInputs.appsToApprovedRate = worst.historicalRate;
      } else if (worst.name === 'Approved → Funded') {
        newInputs.approvedToFundedRate = worst.historicalRate;
      }
    }

    setCalculatorInputs(newInputs);
    setActiveScenario('C');
  }, [pathToGoalData, conversionAnalysis]);

  // Handler for Reset: return all sliders to current values
  const handleScenarioReset = useCallback(() => {
    if (!pathToGoalData) return;
    setCalculatorInputs({
      activityIncrease: 0,
      actToAppsRate: pathToGoalData.activityToAppsRate,
      appsToApprovedRate: pathToGoalData.appsToApprovedRate,
      approvedToFundedRate: pathToGoalData.approvedToFundedRate
    });
    setActiveScenario(null);
  }, [pathToGoalData]);

  // ============================================================
  // REVENUE PACING CHART DATA (Phase 2.3)
  // ============================================================

  const revenuePacingData = useMemo(() => {
    if (!data || !data.tabs || !goals || businessDaysInfo.total === 0) {
      return null;
    }

    const isTeam = selectedRep === 'Team' || selectedRep === 'All Reps';

    // Get revenue goal
    const currentMonthGoals = goals.filter(g => g.Month === goalMonth);
    let revenueGoal = 0;
    if (isTeam) {
      currentMonthGoals
        .filter(g => reps.includes(g.Rep))
        .forEach(g => {
          revenueGoal += parseInt(g.Revenue_Goal) || 0;
        });
    } else {
      const repGoal = currentMonthGoals.find(g => g.Rep === selectedRep);
      revenueGoal = repGoal ? (parseInt(repGoal.Revenue_Goal) || 0) : 0;
    }

    // Get all funded records for this month (Commission + Placement_Fee = revenue)
    // Matches Activity/Funnel tabs which also use Funded tab data
    const fundedRecords = (data.tabs.Funded?.data || []).filter(r => {
      if (!r.Date) return false;
      const recordDate = new Date(r.Date.split('T')[0] + 'T00:00:00');
      const matchesMonth = recordDate.getMonth() === monthIndex;
      const matchesYear = recordDate.getFullYear() === year;
      const matchesRep = isTeam ? reps.includes(r.Rep) : r.Rep === selectedRep;
      return matchesMonth && matchesYear && matchesRep;
    });

    // Build list of business days in the month
    const today = new Date();
    const currentMonthIdx = today.getMonth();
    const currentYear = today.getFullYear();
    const isCurrentMonth = monthIndex === currentMonthIdx && year === currentYear;
    const isPastMonth = (year < currentYear) || (year === currentYear && monthIndex < currentMonthIdx);
    const lastDayOfMonth = new Date(year, monthIndex + 1, 0).getDate();
    const monthShortNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const businessDays = [];
    for (let day = 1; day <= lastDayOfMonth; day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays.push({
          dayNum: businessDays.length + 1,
          date: date.toISOString().split('T')[0],
          dayOfMonth: day,
          isPast: isPastMonth || (isCurrentMonth && day <= today.getDate())
        });
      }
    }

    // Calculate cumulative actual revenue for each business day
    let cumulativeRevenue = 0;
    const chartData = businessDays.map(bd => {
      // Add revenue for this day
      const dayRevenue = fundedRecords
        .filter(r => {
          const recordDate = r.Date?.split('T')[0];
          if (!recordDate) return false;
          const recordDayOfMonth = parseInt(recordDate.split('-')[2]);
          return recordDayOfMonth === bd.dayOfMonth;
        })
        .reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);

      if (bd.isPast) {
        cumulativeRevenue += dayRevenue;
      }

      // Required pace (linear from 0 to goal)
      const requiredPace = (revenueGoal / businessDaysInfo.total) * bd.dayNum;

      // Current pace projection (spans ALL days as linear projection from Day 1)
      const dailyRate = businessDaysInfo.elapsed > 0
        ? cumulativeRevenue / businessDaysInfo.elapsed
        : 0;
      const currentPace = dailyRate * bd.dayNum;

      return {
        day: `${monthShortNames[monthIndex]} ${bd.dayOfMonth}`,
        dayNum: bd.dayNum,
        actual: bd.isPast ? cumulativeRevenue : null,
        requiredPace: Math.round(requiredPace),
        currentPace: Math.round(currentPace),
        isToday: bd.dayNum === businessDaysInfo.elapsed
      };
    });

    // Calculate projected end values
    const currentRevenue = cumulativeRevenue;
    const dailyRate = businessDaysInfo.elapsed > 0 ? currentRevenue / businessDaysInfo.elapsed : 0;
    const projectedEnd = dailyRate * businessDaysInfo.total;

    return {
      chartData,
      revenueGoal,
      currentRevenue,
      projectedEnd,
      gapToGoal: revenueGoal - projectedEnd,
      percentOfGoal: revenueGoal > 0 ? (projectedEnd / revenueGoal) * 100 : 0
    };
  }, [data, goals, month, selectedRep, reps, businessDaysInfo, goalMonth, year]);

  // ============================================================
  // LOADING / ERROR STATES
  // ============================================================

  if (!data || !data.tabs) {
    return (
      <div className="goal-tracking-tab" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <h2 style={{ color: '#64748b' }}>Loading dashboard data...</h2>
      </div>
    );
  }

  if (!goals || goals.length === 0) {
    return (
      <div className="goal-tracking-tab" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <h2 style={{ color: '#f59e0b' }}>No goals data available</h2>
        <p style={{ color: '#64748b' }}>Please ensure goals are configured for {month}</p>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="goal-tracking-tab" style={{ background: '#f8fafc', padding: '20px', margin: '-20px', minHeight: '100vh' }}>
      {/* ========== SCOREBOARD (merged Zone 1 + Zone 2) ========== */}
      {pathToGoalData && kpiMetrics && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '20px 24px 24px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">Z-1</div>}

          {/* Context header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 500 }}>
              {month} &bull; Day {businessDaysInfo.elapsed} of {businessDaysInfo.total} &bull;{' '}
              {selectedRep === 'Team' || selectedRep === 'All Reps' ? 'Team' : selectedRep.split(' ')[0]}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              {pathToGoalData.gapToGoal > 0 ? (
                <>
                  <span style={{ color: '#dc2626', fontWeight: 700 }}>
                    Behind by {formatCurrencyFull(pathToGoalData.gapToGoal)}
                  </span>
                  <span style={{ color: '#94a3b8' }}> — {pathToGoalData.percentOfGoal.toFixed(0)}% to revenue goal</span>
                </>
              ) : (
                <>
                  <span style={{ color: '#16a34a', fontWeight: 700 }}>
                    Ahead by {formatCurrencyFull(Math.abs(pathToGoalData.gapToGoal))}
                  </span>
                  <span style={{ color: '#94a3b8' }}> — {pathToGoalData.percentOfGoal.toFixed(0)}% to revenue goal</span>
                </>
              )}
            </div>
          </div>

          {/* Three metric cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px'
          }}>
            {[
              {
                devLabel: 'SC-1',
                label: 'Revenue',
                current: kpiMetrics.revenue.current,
                projected: pathToGoalData.projectedRevenue,
                goal: kpiMetrics.revenue.goal,
                formatCurrent: formatCurrencyFull,
                formatSmall: formatCurrencyFull,
              },
              {
                devLabel: 'SC-2',
                label: 'Funded Units',
                current: kpiMetrics.fundedUnits.current,
                projected: pathToGoalData.projectedFunded,
                goal: kpiMetrics.fundedUnits.goal,
                formatCurrent: (v) => Math.round(v).toLocaleString(),
                formatSmall: (v) => Math.round(v).toLocaleString(),
              },
              {
                devLabel: 'SC-3',
                label: 'Activity Points',
                current: kpiMetrics.activityPoints.current,
                projected: pathToGoalData.projectedActivity ?? kpiMetrics.activityPoints.expected,
                goal: kpiMetrics.activityPoints.goal,
                formatCurrent: (v) => Math.round(v).toLocaleString(),
                formatSmall: (v) => Math.round(v).toLocaleString(),
              }
            ].map(({ devLabel, label, current, projected, goal, formatCurrent, formatSmall }) => {
              const pct = goal > 0 ? (projected / goal) * 100 : 0;
              const isAhead = pct >= 100;
              const barWidth = Math.min(pct, 100);
              // Current fills solid blue, projected fills lighter if ahead of current
              const currentPct = goal > 0 ? Math.min((current / goal) * 100, 100) : 0;

              return (
                <div key={label} style={{
                  background: '#f8fafc',
                  borderRadius: '10px',
                  padding: '20px',
                  border: '1px solid #e2e8f0',
                  position: 'relative'
                }}>
                  {developerMode && <div className="dev-card-label">{devLabel}</div>}

                  {/* Label */}
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '8px'
                  }}>
                    {label}
                  </div>

                  {/* Hero number */}
                  <div style={{
                    fontSize: '52px',
                    fontWeight: 800,
                    color: '#0f172a',
                    lineHeight: 1,
                    marginBottom: '16px',
                    letterSpacing: '-1px'
                  }}>
                    {formatCurrent(current)}
                  </div>

                  {/* Progress bar: current (solid blue) + projected remainder (light blue) */}
                  <div style={{
                    height: '6px',
                    background: '#e2e8f0',
                    borderRadius: '3px',
                    overflow: 'hidden',
                    marginBottom: '10px',
                    position: 'relative'
                  }}>
                    {/* Projected fill (behind current fill) */}
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${barWidth}%`,
                      background: isAhead ? '#bbf7d0' : '#bfdbfe',
                      borderRadius: '3px'
                    }} />
                    {/* Current fill (on top) */}
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${currentPct}%`,
                      background: isAhead ? '#22c55e' : '#3b82f6',
                      borderRadius: '3px'
                    }} />
                  </div>

                  {/* Bottom row: proj → goal | % */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      proj{' '}
                      <strong style={{ color: '#3b82f6' }}>{formatSmall(Math.round(projected))}</strong>
                      {' '}→ goal{' '}
                      <strong style={{ color: '#10b981' }}>{formatSmall(goal)}</strong>
                    </div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 800,
                      color: isAhead ? '#16a34a' : '#dc2626',
                      letterSpacing: '-0.5px'
                    }}>
                      {pct.toFixed(0)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========== REVENUE PACING CHART (Standalone Section) ========== */}
      {revenuePacingData && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          marginBottom: '20px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">Z-2b</div>}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '12px'
          }}>
            <div style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#1e293b'
            }}>
              Revenue Pacing
            </div>
            <div style={{ fontSize: '16px', color: '#64748b', textAlign: 'right' }}>
              <span style={{ color: '#3b82f6', fontWeight: 600 }}>
                {formatCurrency(revenuePacingData.currentRevenue)}
              </span>
              {' → '}
              <span style={{ color: '#94a3b8' }}>
                {formatCurrency(revenuePacingData.projectedEnd)} proj
              </span>
              {' | Goal: '}
              <span style={{ color: '#10b981', fontWeight: 600 }}>
                {formatCurrency(revenuePacingData.revenueGoal)}
              </span>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={revenuePacingData.chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 10, fill: '#64748b' }}
                tickLine={{ stroke: '#e2e8f0' }}
                interval="preserveStartEnd"
              />
              <YAxis
                tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`}
                tick={{ fontSize: 10, fill: '#64748b' }}
                tickLine={{ stroke: '#e2e8f0' }}
                domain={[0, 'auto']}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (value === null) return ['-', name];
                  return [`$${value.toLocaleString()}`, name];
                }}
                labelFormatter={(label) => label}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />

              {/* Required Pace (dashed green) - the target line */}
              <Line
                type="monotone"
                dataKey="requiredPace"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="8 4"
                name="Required Pace"
                dot={false}
                connectNulls
              />

              {/* Current Pace projection (dashed gray - darker for visibility) */}
              <Line
                type="monotone"
                dataKey="currentPace"
                stroke="#6b7280"
                strokeWidth={2}
                strokeDasharray="6 3"
                name="Current Pace"
                dot={false}
                connectNulls
              />

              {/* Actual Revenue (solid blue) */}
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#3b82f6"
                strokeWidth={3}
                name="Actual"
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (payload.isToday && cy) {
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill="#3b82f6"
                        stroke="white"
                        strokeWidth={2}
                      />
                    );
                  }
                  return null;
                }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>

          {/* Legend */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            marginTop: '12px',
            fontSize: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '24px', height: '3px', background: '#3b82f6', borderRadius: '2px' }} />
              <span>Actual Revenue</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '24px',
                height: '3px',
                background: 'repeating-linear-gradient(90deg, #6b7280 0px, #6b7280 4px, transparent 4px, transparent 8px)',
                borderRadius: '2px'
              }} />
              <span>Current Pace (projected)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '24px',
                height: '3px',
                background: 'repeating-linear-gradient(90deg, #10b981 0px, #10b981 4px, transparent 4px, transparent 8px)',
                borderRadius: '2px'
              }} />
              <span>Required Pace to Goal</span>
            </div>
          </div>

          {/* Gap indicator */}
          {revenuePacingData.gapToGoal > 0 && (
            <div style={{
              textAlign: 'center',
              marginTop: '8px',
              fontSize: '14px',
              color: '#ef4444',
              fontWeight: 500
            }}>
              At current pace, you'll end {formatCurrency(revenuePacingData.gapToGoal)} short of goal ({revenuePacingData.percentOfGoal.toFixed(0)}%)
            </div>
          )}
          {revenuePacingData.gapToGoal <= 0 && (
            <div style={{
              textAlign: 'center',
              marginTop: '8px',
              fontSize: '14px',
              color: '#10b981',
              fontWeight: 500
            }}>
              On track to exceed goal by {formatCurrency(Math.abs(revenuePacingData.gapToGoal))} ({revenuePacingData.percentOfGoal.toFixed(0)}%)
            </div>
          )}
        </div>
      )}

      {/* ========== TODAY'S ACTIVITY (New Section) ========== */}
      {todayMetrics && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          marginBottom: '20px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">Z-2c</div>}

          {/* Section Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <div>
              <div style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#1e293b'
              }}>
                Today's Activity
              </div>
              <div style={{
                fontSize: '13px',
                color: '#64748b',
                marginTop: '2px'
              }}>
                {todayMetrics.showingFriday
                  ? `Showing Friday's data (${todayMetrics.displayDate})`
                  : todayMetrics.displayDate} — {todayMetrics.isAllReps ? 'Team totals' : selectedRep.split(' ')[0]} vs daily goals
              </div>
            </div>
          </div>

          {/* 7-Card Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '12px'
          }}>
            {todayMetrics.metrics.map((metric) => {
              const hasGoal = metric.goal !== null && metric.goal > 0;
              const percentOfGoal = hasGoal ? (metric.actual / metric.goal) * 100 : 0;
              const hasActivityContrib = metric.activityContrib !== undefined;

              // Determine status color
              let statusColor = '#64748b'; // neutral gray
              let statusBg = '#f1f5f9';
              if (metric.isActivity || hasGoal) {
                const pct = hasGoal ? percentOfGoal : 0;
                if (metric.isActivity) {
                  // Activity card gets special blue styling
                  statusBg = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)';
                  statusColor = '#1d4ed8';
                } else if (pct >= 100) {
                  statusColor = '#15803d';
                  statusBg = 'rgba(34, 197, 94, 0.1)';
                } else if (pct >= 80) {
                  statusColor = '#b45309';
                  statusBg = 'rgba(245, 158, 11, 0.1)';
                } else {
                  statusColor = '#dc2626';
                  statusBg = 'rgba(239, 68, 68, 0.1)';
                }
              }

              // Format display value
              const displayValue = metric.isCurrency
                ? (metric.actual >= 1000
                    ? `$${(metric.actual / 1000).toFixed(1)}K`
                    : `$${Math.round(metric.actual).toLocaleString()}`)
                : metric.actual.toLocaleString();

              const displayGoal = metric.isCurrency
                ? (metric.goal >= 1000
                    ? `$${(metric.goal / 1000).toFixed(1)}K`
                    : `$${Math.round(metric.goal).toLocaleString()}`)
                : metric.goal?.toLocaleString();

              return (
                <div key={metric.key} style={{
                  background: statusBg,
                  borderRadius: '10px',
                  padding: '14px 12px',
                  textAlign: 'center',
                  border: metric.isActivity
                    ? '2px solid #3b82f6'
                    : `1px solid ${hasGoal && percentOfGoal >= 100 ? 'rgba(34, 197, 94, 0.3)' : '#e2e8f0'}`,
                  position: 'relative'
                }}>
                  {developerMode && <div className="dev-card-label">TA-{metric.key}</div>}

                  {/* Label */}
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: metric.isActivity ? '#3b82f6' : '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px'
                  }}>
                    {metric.label}
                  </div>

                  {/* Actual Value */}
                  <div style={{
                    fontSize: metric.isActivity ? '32px' : '28px',
                    fontWeight: 800,
                    color: metric.isActivity ? '#1d4ed8' : '#1e293b',
                    lineHeight: 1,
                    marginBottom: (hasGoal || hasActivityContrib) ? '8px' : '0'
                  }}>
                    {displayValue}
                  </div>

                  {/* Progress Bar + Goal (only if has goal) */}
                  {hasGoal && (
                    <>
                      <div style={{
                        height: '6px',
                        background: '#e2e8f0',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        marginBottom: '6px'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(percentOfGoal, 100)}%`,
                          background: metric.isActivity
                            ? '#3b82f6'
                            : percentOfGoal >= 100 ? '#22c55e' : percentOfGoal >= 80 ? '#f59e0b' : '#ef4444',
                          borderRadius: '3px',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: statusColor,
                        fontWeight: 600
                      }}>
                        {percentOfGoal.toFixed(0)}% of {displayGoal}
                      </div>
                    </>
                  )}

                  {/* Activity Contribution (for Calls, SMS, Apps) */}
                  {hasActivityContrib && (
                    <div style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginTop: hasGoal ? '4px' : '0'
                    }}>
                      {metric.appsMultiplier
                        ? `${metric.activityContrib.toFixed(0)}% of activity (×${metric.appsMultiplier})`
                        : `${metric.activityContrib.toFixed(0)}% of activity`
                      }
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========== ZONE 3: THE PIPELINE ========== */}
      {pathToGoalData && (
        <div className="goal-tracking-pipeline" style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          marginBottom: '20px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">Z-3</div>}

          {/* Section Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <div>
              <div style={{
                fontSize: '20px',
                fontWeight: 700,
                color: '#1e293b'
              }}>
                Conversion Pipeline
              </div>
              <div style={{
                fontSize: '14px',
                color: '#64748b',
                marginTop: '2px'
              }}>
                MTD funnel performance with health indicators
              </div>
            </div>
          </div>

          {/* Visual Funnel - Single Row with Complete Info */}
          <div style={{
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            gap: '0',
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '20px 30px'
          }}>
            {[
              { label: 'Activity', value: formatNumber(pathToGoalData.activity), color: '#3b82f6' },
              { label: 'Apps', value: pathToGoalData.apps, color: '#8b5cf6', rate: pathToGoalData.activityToAppsRate, histRate: pathToGoalData.histActToAppsRate },
              { label: 'Approved', value: pathToGoalData.approved, color: '#f59e0b', rate: pathToGoalData.appsToApprovedRate, histRate: pathToGoalData.histAppsToApprovedRate },
              { label: 'Funded', value: pathToGoalData.funded, color: '#10b981', rate: pathToGoalData.approvedToFundedRate, histRate: pathToGoalData.histApprovedToFundedRate },
              { label: 'Revenue', value: formatCurrency(pathToGoalData.revenue), color: '#22c55e' }
            ].map((stage, i, arr) => {
              // Get the NEXT stage's rate info for the arrow (rate shows conversion TO next stage)
              const nextStage = arr[i + 1];

              // Calculate health status and variance for the arrow
              const getHealthInfo = (current, historical) => {
                if (!current || !historical || historical === 0) return null;
                const diff = current - historical;
                const percentDiff = (diff / historical) * 100;

                let color, icon;
                if (percentDiff >= 0) {
                  color = '#22c55e';
                  icon = '✓';
                } else if (percentDiff >= -10) {
                  color = '#f59e0b';
                  icon = '!';
                } else {
                  color = '#ef4444';
                  icon = '✗';
                }

                return { color, icon, diff, percentDiff };
              };

              const healthInfo = nextStage?.rate !== undefined
                ? getHealthInfo(nextStage.rate, nextStage.histRate)
                : null;

              // Special handling for last arrow (Funded → Revenue) to show avg deal size
              const isLastArrow = i === arr.length - 2;
              const avgDealHealth = isLastArrow
                ? getHealthInfo(pathToGoalData.avgDealSize, pathToGoalData.histAvgDealSize)
                : null;

              return (
                <React.Fragment key={stage.label}>
                  {/* Stage Box */}
                  <div style={{
                    flex: '1',
                    maxWidth: '160px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: '100%',
                      height: '80px',
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: stage.color,
                      color: 'white',
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                      <div style={{ fontSize: '26px', fontWeight: 800 }}>{stage.value}</div>
                      <div style={{ fontSize: '11px', fontWeight: 600, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {stage.label}
                      </div>
                    </div>
                  </div>

                  {/* Arrow with complete conversion info */}
                  {i < arr.length - 1 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 12px',
                      minWidth: '100px',
                      flex: '0 1 auto'
                    }}>
                      {/* Arrow */}
                      <div style={{
                        fontSize: '18px',
                        color: '#94a3b8',
                        marginBottom: '2px'
                      }}>→</div>

                      {/* Conversion Rate (for non-last arrows) */}
                      {nextStage?.rate !== undefined && !isLastArrow && (
                        <>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: 700,
                            color: '#1e293b'
                          }}>
                            {nextStage.rate.toFixed(1)}%
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#64748b',
                            marginBottom: '2px'
                          }}>
                            vs {nextStage.histRate?.toFixed(1)}% avg
                          </div>
                          {healthInfo && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '3px',
                              fontSize: '11px',
                              color: healthInfo.color,
                              fontWeight: 600
                            }}>
                              <span>{healthInfo.icon}</span>
                              <span>{healthInfo.diff >= 0 ? '+' : ''}{healthInfo.diff.toFixed(1)}pp</span>
                            </div>
                          )}
                        </>
                      )}

                      {/* Last arrow: show only avg deal size (no conversion rate - Revenue is the final value) */}
                      {isLastArrow && (
                        <>
                          {/* Avg Deal Size */}
                          <div style={{
                            fontSize: '15px',
                            fontWeight: 700,
                            color: '#1e293b'
                          }}>
                            {formatCurrency(pathToGoalData.avgDealSize)}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#64748b'
                          }}>
                            vs {formatCurrency(pathToGoalData.histAvgDealSize)} avg
                          </div>
                          {avgDealHealth && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '3px',
                              fontSize: '11px',
                              color: avgDealHealth.color,
                              fontWeight: 600
                            }}>
                              <span>{avgDealHealth.icon}</span>
                              <span>{avgDealHealth.diff >= 0 ? '+' : ''}{formatCurrency(avgDealHealth.diff)}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* ========== ZONE 4: THE DIAGNOSIS ========== */}
      <div className="goal-tracking-diagnosis" style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        position: 'relative'
      }}>
        {developerMode && <div className="dev-section-label">Z-4</div>}

        {/* Section Header */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#1e293b'
          }}>
            The Diagnosis
          </div>
          <div style={{
            fontSize: '14px',
            color: '#64748b',
            marginTop: '2px'
          }}>
            {pathToGoalData
              ? `Why you're at ${formatCurrencyFull(pathToGoalData.revenue)} of ${formatCurrencyFull(pathToGoalData.revenueGoal)} goal`
              : 'Analyzing your conversion funnel...'}
          </div>
        </div>

        {/* 4-Panel Diagnosis Grid */}
        {conversionAnalysis ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px'
          }}>
            {/* Panel 1: PRIMARY ISSUE (Red) */}
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderLeft: `4px solid ${conversionAnalysis.worstConversion ? '#ef4444' : '#22c55e'}`,
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">D-1</div>}
              {conversionAnalysis.worstConversion ? (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#ef4444',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 700
                    }}>!</div>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: '#dc2626',
                      letterSpacing: '0.5px'
                    }}>Primary Issue</span>
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    {conversionAnalysis.worstConversion.name}
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>
                    {conversionAnalysis.worstConversion.currentRate.toFixed(1)}% vs {conversionAnalysis.worstConversion.historicalRate.toFixed(1)}% avg
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#ef4444'
                  }}>
                    -${conversionAnalysis.worstConversion.revenueImpact.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    ~{Math.round(conversionAnalysis.worstConversion.extraDeals)} lost deals
                  </div>
                </>
              ) : (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#22c55e',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 700
                    }}>✓</div>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: '#16a34a',
                      letterSpacing: '0.5px'
                    }}>No Major Issues</span>
                  </div>
                  <div style={{ fontSize: '14px', color: '#166534', lineHeight: 1.6 }}>
                    All conversion rates are at or above your 6-month average! Focus on increasing activity volume.
                  </div>
                </>
              )}
            </div>

            {/* Panel 2: SECONDARY ISSUES (Amber) */}
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #f59e0b',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">D-2</div>}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#f59e0b',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 700
                }}>⚠</div>
                <span style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: '#b45309',
                  letterSpacing: '0.5px'
                }}>Secondary Issues</span>
              </div>
              {(() => {
                const secondaryIssues = conversionAnalysis.issues?.slice(1) || [];
                if (secondaryIssues.length === 0) {
                  return (
                    <div style={{ fontSize: '14px', color: '#92400e', fontStyle: 'italic' }}>
                      {conversionAnalysis.worstConversion
                        ? 'None - only one issue identified'
                        : 'None - all conversions are healthy!'}
                    </div>
                  );
                }
                return (
                  <>
                    {secondaryIssues.map(issue => (
                      <div key={issue.name} style={{ fontSize: '14px', color: '#374151', padding: '4px 0' }}>
                        • <strong>{issue.name}</strong>: -${issue.revenueImpact.toLocaleString()}
                        <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '6px' }}>
                          ({issue.currentRate.toFixed(1)}% vs {issue.historicalRate.toFixed(1)}%)
                        </span>
                      </div>
                    ))}
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#b45309', marginTop: '12px', paddingTop: '8px', borderTop: '1px solid #fde68a' }}>
                      Total: -${secondaryIssues.reduce((sum, i) => sum + i.revenueImpact, 0).toLocaleString()}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Panel 3: BRIGHT SPOTS (Green) */}
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #22c55e',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">D-3</div>}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#22c55e',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 700
                }}>✓</div>
                <span style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: '#16a34a',
                  letterSpacing: '0.5px'
                }}>Bright Spots</span>
              </div>
              {conversionAnalysis.brightSpots?.length > 0 ? (
                conversionAnalysis.brightSpots.map(spot => (
                  <div key={spot.name} style={{ fontSize: '14px', color: '#374151', padding: '4px 0' }}>
                    • <strong>{spot.name}</strong>: {spot.currentRate.toFixed(1)}%
                    <span style={{ color: '#16a34a', fontWeight: 600, marginLeft: '6px' }}>
                      +{Math.abs(spot.gap).toFixed(1)}pp
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic' }}>
                  None currently - all conversions below average
                </div>
              )}
            </div>

            {/* Panel 4: THE FULL PICTURE (Blue) */}
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #3b82f6',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">D-4</div>}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#3b82f6',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 700
                }}>i</div>
                <span style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: '#1d4ed8',
                  letterSpacing: '0.5px'
                }}>Summary</span>
              </div>
              <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                {(() => {
                  const primary = conversionAnalysis.worstConversion;
                  const brightSpots = conversionAnalysis.brightSpots || [];

                  if (!primary) {
                    return "All conversion rates are healthy! Focus on increasing activity volume to drive more revenue.";
                  }

                  let narrative = `Your ${primary.name} rate is costing ~$${primary.revenueImpact.toLocaleString()} this month. `;
                  narrative += `Return to your ${primary.historicalRate.toFixed(1)}% average for ~${Math.round(primary.extraDeals)} more deals.`;

                  if (brightSpots.length > 0) {
                    narrative += ` Good news: ${brightSpots[0].name} is strong at ${brightSpots[0].currentRate.toFixed(1)}%.`;
                  }

                  return narrative;
                })()}
              </div>
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#94a3b8'
          }}>
            Loading diagnosis...
          </div>
        )}
      </div>

      {/* ========== ZONE 5: SCENARIO CALCULATOR ========== */}
      {pathToGoalData && calculatorProjections && (
        <div className="goal-tracking-scenarios" style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          marginBottom: '20px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">Z-5</div>}

          {/* Section Header */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              fontSize: '22px',
              fontWeight: 700,
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🧪 Scenario Calculator
            </div>
            <div style={{
              fontSize: '15px',
              color: '#64748b',
              marginTop: '2px'
            }}>
              Here's where you're headed. Here's what you can change. Here's what happens if you do.
            </div>
          </div>

          {/* ── SECTION 1: THE SITUATION ── */}
          <div style={{
            background: '#f8fafc',
            borderRadius: '10px',
            padding: '20px',
            marginBottom: '0',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#64748b',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              📊 Where You're Headed
              <span style={{ fontWeight: 400, fontSize: '12px', color: '#94a3b8' }}>(current pace, no changes)</span>
            </div>

            {/* Funnel arrow chain */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '16px',
              flexWrap: 'wrap'
            }}>
              {[
                { label: 'Apps', value: Math.round(pathToGoalData.projectedApps) },
                { label: 'Approved', value: Math.round(pathToGoalData.projectedApproved) },
                { label: 'Funded', value: Math.round(pathToGoalData.projectedFunded) },
                { label: 'Revenue', value: formatCurrency(pathToGoalData.projectedRevenue) }
              ].map((item, i, arr) => (
                <React.Fragment key={item.label}>
                  <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    border: '1px solid #e2e8f0',
                    textAlign: 'center',
                    minWidth: '100px'
                  }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                      {item.value}
                    </div>
                  </div>
                  {i < arr.length - 1 && (
                    <span style={{ fontSize: '18px', color: '#cbd5e1', fontWeight: 700 }}>→</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Goal / Gap / % summary row */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
              fontSize: '14px',
              color: '#64748b'
            }}>
              <span>Goal: <strong style={{ color: '#1e293b' }}>{formatCurrency(pathToGoalData.revenueGoal)}</strong></span>
              <span>Gap: <strong style={{ color: pathToGoalData.gapToGoal > 0 ? '#dc2626' : '#16a34a' }}>
                {pathToGoalData.gapToGoal > 0
                  ? `-${formatCurrency(pathToGoalData.gapToGoal)}`
                  : `+${formatCurrency(Math.abs(pathToGoalData.gapToGoal))}`
                }
              </strong></span>
              <span><strong style={{ color: '#1e293b' }}>{pathToGoalData.percentOfGoal.toFixed(0)}%</strong> of goal</span>
            </div>
          </div>

          {/* ── DIVIDER ── */}
          <div style={{ borderTop: '2px solid #e2e8f0', margin: '24px 0' }} />

          {/* ── SECTION 2: THE LEVERS ── */}
          <div style={{ marginBottom: '0' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1e293b',
              marginBottom: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              🎚️ Pull a Lever
            </div>
            <div style={{
              fontSize: '13px',
              color: '#94a3b8',
              marginBottom: '16px'
            }}>
              Drag any slider to see how it changes your outcome.
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px'
            }}>
              {/* Activity Increase Slider */}
              <EnhancedSlider
                label="Activity Increase"
                icon="📞"
                currentValue={0}
                adjustedValue={calculatorInputs.activityIncrease}
                onAdjust={(val) => {
                  setCalculatorInputs(prev => ({ ...prev, activityIncrease: Math.round(val) }));
                  setActiveScenario(null);
                }}
                historicalAvg={0}
                min={0}
                max={50}
                step={5}
                formatValue={(v) => `+${v}%`}
                formatChange={(v) => `${v > 0 ? '+' : ''}${v}%`}
              />

              {/* Activity → Apps Rate Slider */}
              <EnhancedSlider
                label="Activity → Apps Rate"
                icon="📋"
                currentValue={pathToGoalData.activityToAppsRate}
                adjustedValue={calculatorInputs.actToAppsRate ?? pathToGoalData.activityToAppsRate}
                onAdjust={(val) => {
                  setCalculatorInputs(prev => ({ ...prev, actToAppsRate: val }));
                  setActiveScenario(null);
                }}
                historicalAvg={pathToGoalData.histActToAppsRate}
                min={0.5}
                max={5}
                step={0.1}
                formatValue={(v) => `${v.toFixed(1)}%`}
                formatChange={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}pp`}
              />

              {/* Apps → Approved Rate Slider */}
              <EnhancedSlider
                label="Apps → Approved Rate"
                icon="✅"
                currentValue={pathToGoalData.appsToApprovedRate}
                adjustedValue={calculatorInputs.appsToApprovedRate ?? pathToGoalData.appsToApprovedRate}
                onAdjust={(val) => {
                  setCalculatorInputs(prev => ({ ...prev, appsToApprovedRate: val }));
                  setActiveScenario(null);
                }}
                historicalAvg={pathToGoalData.histAppsToApprovedRate}
                min={20}
                max={90}
                step={1}
                formatValue={(v) => `${v.toFixed(1)}%`}
                formatChange={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}pp`}
              />

              {/* Approved → Funded Rate Slider */}
              <EnhancedSlider
                label="Approved → Funded Rate"
                icon="💰"
                currentValue={pathToGoalData.approvedToFundedRate}
                adjustedValue={calculatorInputs.approvedToFundedRate ?? pathToGoalData.approvedToFundedRate}
                onAdjust={(val) => {
                  setCalculatorInputs(prev => ({ ...prev, approvedToFundedRate: val }));
                  setActiveScenario(null);
                }}
                historicalAvg={pathToGoalData.histApprovedToFundedRate}
                min={20}
                max={90}
                step={1}
                formatValue={(v) => `${v.toFixed(1)}%`}
                formatChange={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}pp`}
              />
            </div>
          </div>

          {/* ── DIVIDER ── */}
          <div style={{ borderTop: '2px solid #e2e8f0', margin: '24px 0' }} />

          {/* ── SECTION 3: THE RESULT ── */}

          {/* Part A — Preset Scenario Cards */}
          {pathToGoalData.gapToGoal > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#1e293b',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                💡 Or try a preset
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px'
              }}>
                {/* Scenario A: Volume Play */}
                <div
                  onClick={handleScenarioA}
                  style={{
                    background: 'white',
                    borderRadius: '10px',
                    padding: '16px',
                    border: activeScenario === 'A' ? '3px solid #3b82f6' : '2px solid #e2e8f0',
                    borderLeft: '4px solid #3b82f6',
                    position: 'relative',
                    cursor: 'pointer',
                    boxShadow: activeScenario === 'A' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {developerMode && <div className="dev-card-label">SC-A</div>}
                  {activeScenario === 'A' && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#3b82f6',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600
                    }}>
                      ACTIVE
                    </div>
                  )}
                  <h5 style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>
                    📞 Volume Play
                  </h5>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', marginBottom: '4px' }}>
                    {formatCurrency(pathToGoalData.scenarioA.projectedTotal)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '6px' }}>month-end</div>
                  <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                    +20% activity<br/>
                    {pathToGoalData.scenarioA.projectedPercent.toFixed(0)}% of goal
                  </div>
                </div>

                {/* Scenario B: Conversion Fix */}
                <div
                  onClick={!pathToGoalData.scenarioB.isAllHealthy ? handleScenarioB : undefined}
                  style={{
                    background: 'white',
                    borderRadius: '10px',
                    padding: '16px',
                    border: activeScenario === 'B' ? '3px solid #22c55e' : '2px solid #e2e8f0',
                    borderLeft: `4px solid ${pathToGoalData.scenarioB.isAllHealthy ? '#22c55e' : '#10b981'}`,
                    position: 'relative',
                    cursor: pathToGoalData.scenarioB.isAllHealthy ? 'default' : 'pointer',
                    opacity: pathToGoalData.scenarioB.isAllHealthy ? 0.7 : 1,
                    boxShadow: activeScenario === 'B' ? '0 0 0 3px rgba(34, 197, 94, 0.2)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {developerMode && <div className="dev-card-label">SC-B</div>}
                  {activeScenario === 'B' && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#22c55e',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600
                    }}>
                      ACTIVE
                    </div>
                  )}
                  {pathToGoalData.scenarioB.isAllHealthy ? (
                    <>
                      <h5 style={{ fontSize: '14px', fontWeight: 600, color: '#22c55e', marginBottom: '6px' }}>
                        🎉 All Healthy
                      </h5>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#22c55e', marginBottom: '6px' }}>
                        No issues!
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                        All conversions at or above average
                      </div>
                    </>
                  ) : (
                    <>
                      <h5 style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>
                        🔧 Fix {pathToGoalData.scenarioB.conversionName}
                      </h5>
                      <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', marginBottom: '4px' }}>
                        {formatCurrency(pathToGoalData.scenarioB.projectedTotal)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '6px' }}>month-end</div>
                      <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                        {pathToGoalData.scenarioB.currentRate.toFixed(1)}% → {pathToGoalData.scenarioB.targetRate.toFixed(1)}%<br/>
                        {pathToGoalData.scenarioB.projectedPercent.toFixed(0)}% of goal
                      </div>
                    </>
                  )}
                </div>

                {/* Scenario C: Hybrid */}
                <div
                  onClick={handleScenarioC}
                  style={{
                    background: 'white',
                    borderRadius: '10px',
                    padding: '16px',
                    border: activeScenario === 'C' ? '3px solid #8b5cf6' : '2px solid #e2e8f0',
                    borderLeft: '4px solid #8b5cf6',
                    position: 'relative',
                    cursor: 'pointer',
                    boxShadow: activeScenario === 'C' ? '0 0 0 3px rgba(139, 92, 246, 0.2)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {developerMode && <div className="dev-card-label">SC-C</div>}
                  {activeScenario === 'C' && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#8b5cf6',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600
                    }}>
                      ACTIVE
                    </div>
                  )}
                  <h5 style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>
                    ⚡ Hybrid
                  </h5>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', marginBottom: '4px' }}>
                    {formatCurrency(pathToGoalData.scenarioC.projectedTotal)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '6px' }}>month-end</div>
                  <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                    Both levers<br/>
                    {pathToGoalData.scenarioC.projectedPercent.toFixed(0)}% of goal
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* On track message when gap is closed */}
          {pathToGoalData.gapToGoal <= 0 && (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '10px',
              padding: '20px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>🎉</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#16a34a' }}>
                On Track to Exceed Goal!
              </div>
              <div style={{ fontSize: '15px', color: '#15803d', marginTop: '4px' }}>
                At current pace, you'll end {formatCurrency(Math.abs(pathToGoalData.gapToGoal))} above goal.
              </div>
            </div>
          )}

          {/* Part B — Outcome Row */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            border: '2px solid #e2e8f0'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1e293b',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              🎯 With This Scenario → Month-End
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '24px'
            }}>
              {/* Funnel metrics */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px',
                flex: 1
              }}>
                {[
                  { label: 'Apps', baseline: pathToGoalData.projectedApps, scenario: calculatorProjections.projApps },
                  { label: 'Approved', baseline: pathToGoalData.projectedApproved, scenario: calculatorProjections.projApproved },
                  { label: 'Funded', baseline: pathToGoalData.projectedFunded, scenario: calculatorProjections.projFunded }
                ].map((card) => {
                  const delta = card.scenario - card.baseline;
                  const deltaColor = delta > 0 ? '#10b981' : delta < 0 ? '#ef4444' : '#64748b';
                  return (
                    <div key={card.label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>
                        {card.label}
                      </div>
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                        {Math.round(card.scenario)}
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: deltaColor }}>
                        {delta >= 0 ? '+' : ''}{Math.round(delta)}{delta > 0 ? '▲' : delta < 0 ? '▼' : ''}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Revenue — hero treatment */}
              {(() => {
                const revDelta = calculatorProjections.projRevenue - pathToGoalData.projectedRevenue;
                const revDeltaColor = revDelta > 0 ? '#10b981' : revDelta < 0 ? '#ef4444' : '#64748b';
                return (
                  <div style={{
                    textAlign: 'center',
                    borderLeft: '2px solid #e2e8f0',
                    paddingLeft: '24px',
                    minWidth: '160px'
                  }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>
                      REVENUE
                    </div>
                    <div style={{
                      fontSize: '36px',
                      fontWeight: 800,
                      color: calculatorProjections.gapRemaining > 0 ? '#dc2626' : '#16a34a',
                      lineHeight: 1,
                      marginBottom: '4px'
                    }}>
                      {formatCurrency(calculatorProjections.projRevenue)}
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: revDeltaColor }}>
                      {revDelta >= 0 ? '+' : ''}{formatCurrency(Math.abs(revDelta))}{revDelta > 0 ? '▲' : revDelta < 0 ? '▼' : ''}
                    </div>
                  </div>
                );
              })()}

              {/* Gap — prominent display */}
              <div style={{
                textAlign: 'center',
                borderLeft: '2px solid #e2e8f0',
                paddingLeft: '24px',
                minWidth: '140px'
              }}>
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>
                  GAP
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: calculatorProjections.gapRemaining > 0 ? '#dc2626' : '#16a34a',
                  lineHeight: 1,
                  marginBottom: '4px'
                }}>
                  {calculatorProjections.gapRemaining > 0
                    ? `-${formatCurrency(calculatorProjections.gapRemaining)}`
                    : `+${formatCurrency(Math.abs(calculatorProjections.gapRemaining))}`
                  }
                </div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>
                  {calculatorProjections.percentToGoal.toFixed(0)}% to goal
                </div>
              </div>
            </div>
          </div>

          {/* Part C — Reset Button */}
          {activeScenario && (
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button
                onClick={handleScenarioReset}
                style={{
                  padding: '6px 16px',
                  background: 'transparent',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#64748b',
                  fontWeight: 500
                }}
              >
                ↩️ Reset to current rates
              </button>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* DAILY BREAKDOWN SECTION (copied from This Week tab)         */}
      {/* ============================================================ */}
      {weekData && weeklyMetrics && (
        <div className="daily-breakdown-container" style={{
          marginTop: '40px',
          padding: '24px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {/* Collapsible Header */}
          <div
            onClick={() => setDailyBreakdownCollapsed(!dailyBreakdownCollapsed)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              paddingBottom: '12px',
              borderBottom: !dailyBreakdownCollapsed ? '2px solid #e2e8f0' : 'none'
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                📊 Daily Breakdown
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                Day-by-day performance with rep drill-down
              </p>
            </div>
            <span style={{
              fontSize: '20px',
              color: '#64748b',
              transform: dailyBreakdownCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}>▼</span>
          </div>

          {!dailyBreakdownCollapsed && (
            <div style={{ marginTop: '16px', overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Day</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Calls</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>SMS</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Apps</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Approved</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Funded</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Revenue</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Activity</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {daysOfWeek.map(day => {
                    const dayData = weekData[day.name];
                    const dayStatus = day.isFuture ? 'future' :
                                    day.isToday ? (dayData.activityCount >= weeklyMetrics.dailyTargets.calls ? 'on-track' : 'behind') :
                                    (dayData.activityCount >= weeklyMetrics.dailyTargets.calls ? 'good' : 'slow');
                    const isExpanded = expandedDay === day.fullDate;
                    const canExpand = isAllReps && !day.isFuture;

                    return (
                      <React.Fragment key={day.name}>
                        <tr
                          onClick={() => canExpand && toggleDayExpansion(day.fullDate)}
                          style={{
                            cursor: canExpand ? 'pointer' : 'default',
                            backgroundColor: day.isToday ? '#eff6ff' : day.isFuture ? '#f9fafb' : 'white',
                            borderBottom: '1px solid #e2e8f0'
                          }}
                        >
                          <td style={{ padding: '12px 8px' }}>
                            <strong>{day.shortName}</strong>
                            <br />
                            <span style={{ fontSize: '11px', color: '#64748b' }}>{day.date}</span>
                            {canExpand && <span style={{ marginLeft: '6px', color: '#3b82f6' }}>{isExpanded ? '▼' : '▶'}</span>}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{day.isFuture ? '-' : dayData.calls}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{day.isFuture ? '-' : dayData.sms}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{day.isFuture ? '-' : dayData.appsIn}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{day.isFuture ? '-' : dayData.approved}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{day.isFuture ? '-' : dayData.funded}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{day.isFuture ? '-' : formatCurrency(dayData.revenue)}</td>
                          <td style={{
                            padding: '12px 8px',
                            textAlign: 'center',
                            backgroundColor: day.isFuture ? 'transparent' :
                              weeklyMetrics.activityCallGoal === 0 ? 'transparent' :
                              dayData.activityCount >= weeklyMetrics.activityCallGoal ? '#d1fae5' :
                              dayData.activityCount >= weeklyMetrics.activityCallGoal * 0.8 ? '#fef3c7' : '#fee2e2',
                            fontWeight: '600'
                          }}>
                            {day.isFuture ? '-' : `${dayData.activityCount}/${weeklyMetrics.activityCallGoal}`}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            {day.isFuture ? '⏳ Future' :
                             day.isToday ? (dayStatus === 'on-track' ? '✅ On Track' : '🔴 Behind') :
                             dayStatus === 'good' ? '✅ Good' : '⚠️ Slow'}
                          </td>
                        </tr>

                        {/* Expandable per-rep breakdown row */}
                        {isExpanded && canExpand && (
                          <tr>
                            <td colSpan="9" style={{ padding: '16px', backgroundColor: '#f8fafc' }}>
                              <div style={{ marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
                                Per-Rep Breakdown for {day.shortName} {day.date}
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#e2e8f0' }}>
                                    <th style={{ padding: '8px', textAlign: 'left' }}>Rep</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Calls</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>SMS</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Apps</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Approved</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Funded</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Revenue</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Activity</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getRepBreakdownForDay(day.fullDate)
                                    .sort((a, b) => b.activityCount - a.activityCount)
                                    .map(repData => (
                                    <tr key={repData.rep} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                      <td style={{ padding: '8px' }}><strong>{repData.rep.split(' ')[0]}</strong></td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{repData.calls}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{repData.sms}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{repData.appsIn}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{repData.approved}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{repData.funded}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{formatCurrency(repData.revenue)}</td>
                                      <td style={{
                                        padding: '8px',
                                        textAlign: 'center',
                                        backgroundColor:
                                          repData.activityGoal === 0 ? 'transparent' :
                                          repData.activityCount >= repData.activityGoal ? '#d1fae5' :
                                          repData.activityCount >= repData.activityGoal * 0.8 ? '#fef3c7' : '#fee2e2',
                                        fontWeight: '600'
                                      }}>
                                        {repData.activityGoal > 0 ? `${repData.activityCount}/${repData.activityGoal}` : repData.activityCount}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* TOTAL Row */}
                  {(() => {
                    const isTotalExpanded = expandedDay === 'TOTAL';
                    const canExpandTotal = isAllReps;

                    return (
                      <React.Fragment>
                        <tr
                          onClick={() => canExpandTotal && setExpandedDay(isTotalExpanded ? null : 'TOTAL')}
                          style={{
                            cursor: canExpandTotal ? 'pointer' : 'default',
                            backgroundColor: '#f1f5f9',
                            fontWeight: 700,
                            borderTop: '2px solid #cbd5e1'
                          }}
                        >
                          <td style={{ padding: '12px 8px' }}>
                            <strong>TOTAL</strong>
                            {canExpandTotal && <span style={{ marginLeft: '6px', color: '#3b82f6' }}>{isTotalExpanded ? '▼' : '▶'}</span>}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}><strong>{weeklyMetrics.totals.calls}</strong></td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}><strong>{weeklyMetrics.totals.sms}</strong></td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}><strong>{weeklyMetrics.totals.apps}</strong></td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}><strong>{weeklyMetrics.totals.approved}</strong></td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}><strong>{weeklyMetrics.totals.funded}</strong></td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}><strong>{formatCurrency(weeklyMetrics.totals.revenue)}</strong></td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}><strong>{weeklyMetrics.totals.activityCount}</strong></td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>-</td>
                        </tr>

                        {/* Expandable per-rep breakdown for Total (weekly totals) */}
                        {isTotalExpanded && canExpandTotal && (
                          <tr>
                            <td colSpan="9" style={{ padding: '16px', backgroundColor: '#f8fafc' }}>
                              <div style={{ marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
                                Per-Rep Weekly Totals
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#e2e8f0' }}>
                                    <th style={{ padding: '8px', textAlign: 'left' }}>Rep</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Calls</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>SMS</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Apps</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Approved</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Funded</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Revenue</th>
                                    <th style={{ padding: '8px', textAlign: 'center' }}>Activity</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getRepBreakdownForWeek()
                                    .sort((a, b) => b.activityCount - a.activityCount)
                                    .map(repData => (
                                      <tr key={repData.rep} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '8px' }}><strong>{repData.rep.split(' ')[0]}</strong></td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>{repData.calls}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>{repData.sms}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>{repData.appsIn}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>{repData.approved}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>{repData.funded}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>{formatCurrency(repData.revenue)}</td>
                                        <td style={{
                                          padding: '8px',
                                          textAlign: 'center',
                                          backgroundColor:
                                            repData.activityGoal === 0 ? 'transparent' :
                                            repData.activityCount >= repData.activityGoal ? '#d1fae5' :
                                            repData.activityCount >= repData.activityGoal * 0.8 ? '#fef3c7' : '#fee2e2',
                                          fontWeight: '600'
                                        }}>
                                          {repData.activityGoal > 0 ? `${repData.activityCount}/${repData.activityGoal}` : repData.activityCount}
                                        </td>
                                      </tr>
                                    ))
                                  }
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
